/**
 * Network bodies arrive in ~16-64 KiB chunks. Every `write()` on a File
 * System Access stream is a round trip to the browser process, so a
 * multi-GB save issued at that granularity is dominated by IPC and takes
 * far longer than the transfer itself. Batching into a few MiB per write
 * keeps memory bounded while cutting the call count by two orders of
 * magnitude.
 */

/** Target size of one batched write. */
export const WRITE_COALESCE_BYTES = 4 * 1024 * 1024;

export class ChunkCoalescer {
  private parts: Uint8Array[] = [];
  private bytes = 0;

  constructor(private readonly flushAt: number = WRITE_COALESCE_BYTES) {}

  /**
   * Returns a batch to write when enough has accumulated, else null.
   * Batches are always ArrayBuffer-backed, which File System Access
   * `write()` requires.
   */
  push(chunk: Uint8Array): Uint8Array<ArrayBuffer> | null {
    if (chunk.byteLength === 0) return null;
    if (this.bytes === 0 && chunk.byteLength >= this.flushAt) {
      return chunk.buffer instanceof ArrayBuffer
        ? (chunk as Uint8Array<ArrayBuffer>)
        : new Uint8Array(chunk);
    }
    this.parts.push(chunk);
    this.bytes += chunk.byteLength;
    return this.bytes >= this.flushAt ? this.drain() : null;
  }

  /** Everything buffered so far, or null when empty. */
  drain(): Uint8Array<ArrayBuffer> | null {
    if (this.bytes === 0) return null;
    const out = new Uint8Array(this.bytes);
    let o = 0;
    for (const part of this.parts) {
      out.set(part, o);
      o += part.byteLength;
    }
    this.parts = [];
    this.bytes = 0;
    return out;
  }
}

export function createCoalescingTransform(
  flushAt: number = WRITE_COALESCE_BYTES,
): TransformStream<Uint8Array, Uint8Array> {
  const coalescer = new ChunkCoalescer(flushAt);
  return new TransformStream<Uint8Array, Uint8Array>({
    transform(chunk, controller) {
      const batch = coalescer.push(chunk);
      if (batch) controller.enqueue(batch);
    },
    flush(controller) {
      const rest = coalescer.drain();
      if (rest) controller.enqueue(rest);
    },
  });
}
